import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { CompleteBlueIcon } from "../../../../assets";
import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, TaskPanelData } from "../../../../models";
import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../service/query";
import { getTaskBoard } from "../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject } from "../../../../utils";
import { getProjectTasksRoute, getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../widget";
import { AvatarCellFormatter } from "../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { ActivityDashboardContext } from "../ActivityDashboardContext";

interface UseTasksReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  projectUuid: string | undefined;
  projectTypeUuid: string | undefined;

  showSelectWorkflowTemplateModal: boolean;
  setShowSelectWorkflowTemplateModal: Dispatch<SetStateAction<boolean>>;

  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;

  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: Dispatch<SetStateAction<boolean>>;
  handleCloseWorkflowTaskPanel: () => void;

  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  selectedCompleteTask: TaskPanelData | undefined;

  workflow: GetTaskBoardResponse["results"][number] | undefined;

  isGroupActivity: boolean;

  refreshTasks: boolean;
  handleRefresh: () => void;

  navigateToTasksBoard: () => void;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useTasks = (): UseTasksReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const { activityDetails } = useContext(ActivityDashboardContext);

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number] | undefined>();

  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showSelectWorkflowTemplateModal, setShowSelectWorkflowTemplateModal] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const handleCloseCompleteTaskModal = (): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  };

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const handleRefresh = (): void => {
    setRefreshTasks((prev) => !prev);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "nameLink",
      dataType: "string",
      formatter: "buttonLink",
      minWidth: 300,
      filterable: true,
      sortable: true,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
    },
    {
      key: "status",
      name: "Status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
      minWidth: 140,
    },
    {
      key: "deadlineDate",
      name: "Deadline",
      dataType: "Date",
      formatter: "dateOnlyWithIndicator",
      minWidth: 140,
      filterable: false,
      sortable: true,
    },
    {
      key: "updatedAt",
      name: "Date updated",
      dataType: "Date",
      formatter: "dateOnly",
      filterable: false,
      minWidth: 140,
    },
    {
      name: "Complete",
      key: "completeIcon",
      dataType: "boolean",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
  ];

  if (activityDetails?.groupUuid != null) {
    columns.splice(1, 0, {
      name: "Project",
      key: "projectLink",
      dataType: "string",
      formatter: "link",
      filterable: true,
      sortable: true,
      minWidth: 180,
    });
  }

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

  const navigateToTasksBoard = (): void => {
    if (workflow) {
      navigate(getWorkflowTasksBoardRoute(workflow.uuid, currentUserType));
    }
  };

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };

        result.avatar = { fullName: d.assignee?.name };
        result.nameLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (activityDetails?.groupUuid != null && d.workflowObjectUuid != null) {
          result.projectLink = {
            to: getProjectTasksRoute(d.workflowObjectUuid, currentUserType),
            text: d.workflowObjectName,
          } as DataGridLinkCellFormatterData;
        }

        if (d.status !== TaskConstants.TASK_STATUS_UNASSIGNED)
          result.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: d.deadlineDate,
                isMandatory: d.isMandatory,
                isMilestone: d.isMilestone,
                keyDate: d.keyDate,
                status: d.status,
                repeat: d.repeat,
                description: d.description,
                name: d.name,
                visibility: [],
                assignedToUserUuid: d.assignee?.uuid ?? null,
                assignedToUserName: d.assignee?.name ?? null,
                ownerOrganisationUuid: d.ownerOrganisation.uuid,
                relateToActivity: d.objectType === "Activity",
                activityUuid: d.objectType === "Activity" ? d.objectUuid : null,
                objectType: d.objectType,
                objectUuid: d.objectUuid,
                stepUuid: d.step?.uuid ?? null,
                phase: d.step?.phase ?? null,
                uuid: d.uuid,
                rowVersion: d.rowVersion,
                resolution: d.resolution,
                reminderDate: d.reminderDate,
                reminderType: d.reminderType,
                addReminder: d.reminderDate != null,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (activityDetails === undefined) {
      return data;
    }

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      filterCriteria.nameLink = undefined;
      delete filterCriteria.nameLink;
    }

    if (filterCriteria.projectLink) {
      filterCriteria.projectName = filterCriteria.projectLink;
      filterCriteria.projectLink = undefined;
      delete filterCriteria.projectLink;
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    filterCriteria.objectUuid = {
      operator: "eq",
      value: activityDetails?.uuid,
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      avatar: "assignee.name",
      objectLink: "workflowObjectName",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  const getAndSetWorkflow = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let filterResults: any = {
      projectUuid: {
        operator: "eq",
        value: activityDetails?.project.uuid,
      },
    };

    // If in group, filter by group
    if (activityDetails?.groupUuid) {
      filterResults = {
        ...filterResults,
        groupUuid: {
          operator: "eq",
          value: activityDetails.groupUuid || null,
        },
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterGroups: any = [
      {
        operator: "or",
        filter: {
          results: filterResults,
        },
      },
    ];

    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filterGroups,
    });

    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  };

  useEffect(() => {
    if (activityDetails?.uuid) {
      getAndSetWorkflow();
    }
  }, [activityDetails?.uuid]);

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,

    showCompleteTaskModal,
    handleCloseCompleteTaskModal,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,

    showSelectWorkflowTemplateModal,
    setShowSelectWorkflowTemplateModal,

    selectedPanelTask,
    selectedCompleteTask,
    workflow,

    isGroupActivity: activityDetails?.groupUuid !== null,
    projectTypeUuid: activityDetails?.project.type?.uuid,
    projectUuid: activityDetails?.project.uuid,

    refreshTasks,
    handleRefresh,

    navigateToTasksBoard,
    onChange,
  };
};
